import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import * as api from '../../api';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import { useTranslation } from 'react-i18next';
import { FD100MultiSelectOptions } from '../Utils/Listings';

dayjs.locale('fr');

const DetailContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const BackButton = styled.button`
  background: #6c757d;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  margin-bottom: 20px;
  cursor: pointer;

  &:hover {
    background: #5a6268;
  }
`;

const HeaderSection = styled.div`
  background: white;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
`;

const Title = styled.h1`
  color: #333;
  margin-bottom: 20px;
`;

const MetaInfo = styled.div`
  display: flex;
  gap: 30px;
  margin-bottom: 20px;
  flex-wrap: wrap;

  > div {
    flex: 1;
    min-width: 200px;
  }
`;

const MetaLabel = styled.span`
  color: #666;
  font-size: 14px;
  display: block;
  margin-bottom: 5px;
`;

const MetaValue = styled.span`
  color: #333;
  font-size: 16px;
  font-weight: 500;
`;

const Section = styled.div`
  background: white;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
`;

const SectionTitle = styled.h2`
  color: #0000FF;
  margin-bottom: 20px;
  font-size: 1.5rem;
`;

const DataGrid = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 15px;
  margin-bottom: 15px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const DataLabel = styled.div`
  font-weight: 600;
  color: #555;
`;

const DataValue = styled.div`
  color: #333;
  white-space: pre-wrap;
`;

const PieceCard = styled.div`
  background: #f8f9fa;
  padding: 25px;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 1px solid #e0e0e0;
`;

const PieceTitle = styled.h3`
  color: #333;
  margin-bottom: 20px;
`;

const MediaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
  margin-top: 20px;
`;

const MediaCard = styled.div`
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const MediaPreview = styled.div`
  width: 100%;
  height: 150px;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const MediaInfo = styled.div`
  padding: 10px;
  font-size: 12px;

  .filename {
    font-weight: 500;
    color: #333;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .size {
    color: #666;
    margin-top: 5px;
  }
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
`;

const UserFormSubmissionDetail = ({ fullUser }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation('common');
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { typologieObjetFD100, tyoplogieEspaceFD100 } = FD100MultiSelectOptions;

  const getTypologyLabel = (value) => {
    if (!value) return '';

    // Check in object typologies
    const objetType = typologieObjetFD100(i18n.language).find(t => t.value === value);
    if (objetType) return objetType.label;

    // Check in space typologies (note: typo in original function name)
    const espaceType = tyoplogieEspaceFD100(i18n.language).find(t => t.value === value);
    if (espaceType) return espaceType.label;

    return value;
  };

  useEffect(() => {
    fetchSubmission();
  }, [id]);

  const fetchSubmission = async () => {
    try {
      setLoading(true);
      // First get user submissions to verify ownership
      const userSubmissions = await api.getUserFormSubmissions(fullUser?.user?._id);
      const userSubmission = userSubmissions.data.submissions.find(s => s._id === id);

      if (!userSubmission) {
        setError(i18n.language === 'fr'
          ? 'Vous n\'avez pas accès à cette soumission'
          : 'You do not have access to this submission');
        return;
      }

      setSubmission(userSubmission);
    } catch (err) {
      console.error('Error fetching submission:', err);
      setError(i18n.language === 'fr'
        ? 'Erreur lors du chargement de la soumission'
        : 'Error loading submission');
    } finally {
      setLoading(false);
    }
  };

  const handleMediaClick = (mediaUrl) => {
    window.open(mediaUrl, '_blank');
  };

  if (loading) {
    return (
      <DetailContainer>
        <LoadingSpinner>
          {i18n.language === 'fr' ? 'Chargement de la soumission...' : 'Loading submission...'}
        </LoadingSpinner>
      </DetailContainer>
    );
  }

  if (error || !submission) {
    return (
      <DetailContainer>
        <p style={{ color: 'red' }}>{error || (i18n.language === 'fr' ? 'Soumission non trouvée' : 'Submission not found')}</p>
        <BackButton onClick={() => navigate('/mycandidatures')}>
          ← {i18n.language === 'fr' ? 'Retour à mes candidatures' : 'Back to my applications'}
        </BackButton>
      </DetailContainer>
    );
  }

  const { data } = submission;
  const mediaBaseUrl = process.env.REACT_APP_MEDIA_BASE_URL;

  return (
    <DetailContainer>
      <BackButton onClick={() => navigate('/mycandidatures')}>
        ← {i18n.language === 'fr' ? 'Retour à mes candidatures' : 'Back to my applications'}
      </BackButton>

      <HeaderSection>
        <Title>
          {i18n.language === 'fr' ? 'Candidature Milan 2026 - ' : 'Milan 2026 Application - '}
          {data.userType === 'designer' ? data.designerCompanyName : data.fabricantCompanyName}
        </Title>
        <MetaInfo>
          <div>
            <MetaLabel>{i18n.language === 'fr' ? 'Date de soumission' : 'Submission date'}</MetaLabel>
            <MetaValue>{dayjs(submission.createdAt).format('DD MMMM YYYY à HH:mm')}</MetaValue>
          </div>
          <div>
            <MetaLabel>{i18n.language === 'fr' ? 'ID de soumission' : 'Submission ID'}</MetaLabel>
            <MetaValue>{submission._id}</MetaValue>
          </div>
        </MetaInfo>
      </HeaderSection>

      <Section>
        <SectionTitle>{i18n.language === 'fr' ? 'Contact du candidat' : 'Applicant Contact'}</SectionTitle>
        <DataGrid>
          <DataLabel>{i18n.language === 'fr' ? 'Prénom:' : 'First name:'}</DataLabel>
          <DataValue>{data.firstName}</DataValue>

          <DataLabel>{i18n.language === 'fr' ? 'Nom:' : 'Last name:'}</DataLabel>
          <DataValue>{data.lastName}</DataValue>

          <DataLabel>Email:</DataLabel>
          <DataValue>{data.email}</DataValue>

          <DataLabel>{i18n.language === 'fr' ? 'Téléphone:' : 'Phone:'}</DataLabel>
          <DataValue>{data.phoneNumber}</DataValue>

          <DataLabel>{i18n.language === 'fr' ? 'Type d\'utilisateur:' : 'User type:'}</DataLabel>
          <DataValue>{data.userType === 'designer' ? 'Designer' : (i18n.language === 'fr' ? 'Fabricant/Éditeur' : 'Manufacturer/Publisher')}</DataValue>
        </DataGrid>
      </Section>

      {data.userType === 'designer' ? (
        <Section>
          <SectionTitle>{i18n.language === 'fr' ? 'Informations du designer' : 'Designer Information'}</SectionTitle>
          <DataGrid>
            <DataLabel>{i18n.language === 'fr' ? 'Nom de l\'entreprise:' : 'Company name:'}</DataLabel>
            <DataValue>{data.designerCompanyName}</DataValue>

            <DataLabel>{i18n.language === 'fr'
              ? 'L\'entreprise est-elle présente à l\'internationale (à travers un showroom, un distributeur, un salon, une exposition, etc.) ?'
              : 'Does the company have an international presence (through a showroom, distributor, trade show, exhibition, etc.)?'}
            </DataLabel>
            <DataValue>{data.designerInternationalPresence}</DataValue>

            {data.designerInternationalDetails && (
              <>
                <DataLabel>{i18n.language === 'fr' ? 'Détails présence internationale:' : 'International presence details:'}</DataLabel>
                <DataValue>{data.designerInternationalDetails}</DataValue>
              </>
            )}

            <DataLabel>{i18n.language === 'fr'
              ? 'L\'entreprise a-t-elle un site internet à jour, une activité sur les réseaux sociaux dont Instagram ?'
              : 'Does the company have an up-to-date website and social media activity including Instagram?'}
            </DataLabel>
            <DataValue>{data.designerWebAndSocial}</DataValue>

            {data.designerWebAndSocialDetails && (
              <>
                <DataLabel>{i18n.language === 'fr' ? 'Détails web et réseaux sociaux:' : 'Web and social media details:'}</DataLabel>
                <DataValue>{data.designerWebAndSocialDetails}</DataValue>
              </>
            )}
          </DataGrid>

          <DataGrid style={{ marginTop: '20px' }}>
            <DataLabel>{i18n.language === 'fr' ? 'Présentation:' : 'Presentation:'}</DataLabel>
            <DataValue>{data.designerPresentation}</DataValue>
          </DataGrid>
        </Section>
      ) : (
        <Section>
          <SectionTitle>{i18n.language === 'fr' ? 'Informations du fabricant/éditeur' : 'Manufacturer/Publisher Information'}</SectionTitle>
          <DataGrid>
            <DataLabel>{i18n.language === 'fr' ? 'Nom de l\'entreprise:' : 'Company name:'}</DataLabel>
            <DataValue>{data.fabricantCompanyName}</DataValue>

            <DataLabel>{i18n.language === 'fr' ? 'L\'entreprise est-elle cotisante au CODIFAB ?' : 'Is the company a CODIFAB contributor?'}</DataLabel>
            <DataValue>{data.fabricantCodifab}</DataValue>

            <DataLabel>{i18n.language === 'fr' ? 'L\'entreprise est-elle adhérente à l\'Ameublement français ?' : 'Is the company a member of Ameublement français?'}</DataLabel>
            <DataValue>{data.fabricantAmeublement}</DataValue>

            <DataLabel>{i18n.language === 'fr'
              ? 'L\'entreprise est-elle présente à l\'internationale (à travers un showroom, un distributeur, un salon, une exposition, etc.) ?'
              : 'Does the company have an international presence (through a showroom, distributor, trade show, exhibition, etc.)?'}
            </DataLabel>
            <DataValue>{data.fabricantInternationalPresence}</DataValue>

            {data.fabricantInternationalDetails && (
              <>
                <DataLabel>{i18n.language === 'fr' ? 'Détails présence internationale:' : 'International presence details:'}</DataLabel>
                <DataValue>{data.fabricantInternationalDetails}</DataValue>
              </>
            )}

            <DataLabel>{i18n.language === 'fr'
              ? 'L\'entreprise a-t-elle un site internet à jour, une activité sur les réseaux sociaux dont Instagram ?'
              : 'Does the company have an up-to-date website and social media activity including Instagram?'}
            </DataLabel>
            <DataValue>{data.fabricantWebAndSocial}</DataValue>

            {data.fabricantWebAndSocialDetails && (
              <>
                <DataLabel>{i18n.language === 'fr' ? 'Détails web et réseaux sociaux:' : 'Web and social media details:'}</DataLabel>
                <DataValue>{data.fabricantWebAndSocialDetails}</DataValue>
              </>
            )}
          </DataGrid>

          <DataGrid style={{ marginTop: '20px' }}>
            <DataLabel>{i18n.language === 'fr' ? 'Présentation:' : 'Presentation:'}</DataLabel>
            <DataValue>{data.fabricantPresentation}</DataValue>
          </DataGrid>
        </Section>
      )}

      {data.certificationAccepted && (
        <Section>
          <SectionTitle>Certification</SectionTitle>
          <DataValue>
            ✓ {i18n.language === 'fr'
              ? 'Le candidat a certifié que les informations fournies sont exactes et véridiques.'
              : 'The applicant has certified that the information provided is accurate and truthful.'}
          </DataValue>
        </Section>
      )}

      <Section>
        <SectionTitle>
          {i18n.language === 'fr' ? 'Pièces proposées' : 'Proposed Pieces'} ({data.pieces?.length || 0})
        </SectionTitle>

        {data.pieces?.map((piece, index) => (
          <PieceCard key={index}>
            <PieceTitle>
              {i18n.language === 'fr' ? `Pièce ${index + 1}: ` : `Piece ${index + 1}: `}{piece.name}
            </PieceTitle>

            <DataGrid>
              <DataLabel>{i18n.language === 'fr' ? 'Typologie:' : 'Typology:'}</DataLabel>
              <DataValue>{piece.typologie === 'autre' && piece.typologieOther ? piece.typologieOther : getTypologyLabel(piece.typologie)}</DataValue>

              <DataLabel>{i18n.language === 'fr'
                ? 'Précisez la date de création de la pièce (maximum 2 ans d\'ancienneté soit à partir d\'avril 2024). Merci de préciser si la pièce est en cours de création.'
                : 'Specify the creation date of the piece (maximum 2 years old, i.e., from April 2024). Please specify if the piece is still being created.'}
              </DataLabel>
              <DataValue>{piece.creationDate}</DataValue>

              {/* Designer information for piece */}
              {data.userType === 'designer' ? (
                <>
                  <DataLabel>Designer:</DataLabel>
                  <DataValue>{data.designerCompanyName}</DataValue>
                </>
              ) : piece.designerCompanyName && (
                <>
                  <DataLabel>{i18n.language === 'fr' ? 'Designer - Nom de l\'entreprise:' : 'Designer - Company name:'}</DataLabel>
                  <DataValue>{piece.designerCompanyName}</DataValue>

                  {piece.designerInternationalPresence && (
                    <>
                      <DataLabel>{i18n.language === 'fr'
                        ? 'Designer - L\'entreprise est-elle présente à l\'internationale ?'
                        : 'Designer - Does the company have an international presence?'}
                      </DataLabel>
                      <DataValue>{piece.designerInternationalPresence}</DataValue>
                    </>
                  )}

                  {piece.designerInternationalDetails && (
                    <>
                      <DataLabel>{i18n.language === 'fr' ? 'Designer - Détails international:' : 'Designer - International details:'}</DataLabel>
                      <DataValue>{piece.designerInternationalDetails}</DataValue>
                    </>
                  )}

                  {piece.designerWebAndSocial && (
                    <>
                      <DataLabel>{i18n.language === 'fr'
                        ? 'Designer - L\'entreprise a-t-elle un site internet à jour, une activité sur les réseaux sociaux dont Instagram ?'
                        : 'Designer - Does the company have an up-to-date website and social media activity including Instagram?'}
                      </DataLabel>
                      <DataValue>{piece.designerWebAndSocial}</DataValue>
                    </>
                  )}

                  {piece.designerWebAndSocialDetails && (
                    <>
                      <DataLabel>{i18n.language === 'fr' ? 'Designer - Détails web/social:' : 'Designer - Web/social details:'}</DataLabel>
                      <DataValue>{piece.designerWebAndSocialDetails}</DataValue>
                    </>
                  )}

                  {piece.designerPresentation && (
                    <>
                      <DataLabel>{i18n.language === 'fr' ? 'Designer - Présentation:' : 'Designer - Presentation:'}</DataLabel>
                      <DataValue>{piece.designerPresentation}</DataValue>
                    </>
                  )}
                </>
              )}

              {/* Fabricant information for piece */}
              {data.userType === 'fabricant' ? (
                <>
                  <DataLabel>{i18n.language === 'fr' ? 'Fabricant/Éditeur:' : 'Manufacturer/Publisher:'}</DataLabel>
                  <DataValue>{data.fabricantCompanyName}</DataValue>
                </>
              ) : piece.fabricantCompanyName && (
                <>
                  <DataLabel>{i18n.language === 'fr' ? 'Fabricant - Nom de l\'entreprise:' : 'Manufacturer - Company name:'}</DataLabel>
                  <DataValue>{piece.fabricantCompanyName}</DataValue>

                  {piece.fabricantCodifab && (
                    <>
                      <DataLabel>{i18n.language === 'fr' ? 'Fabricant - L\'entreprise est-elle cotisante au CODIFAB ?' : 'Manufacturer - Is the company a CODIFAB contributor?'}</DataLabel>
                      <DataValue>{piece.fabricantCodifab}</DataValue>
                    </>
                  )}

                  {piece.fabricantAmeublement && (
                    <>
                      <DataLabel>{i18n.language === 'fr' ? 'Fabricant - L\'entreprise est-elle adhérente à l\'Ameublement français ?' : 'Manufacturer - Is the company a member of Ameublement français?'}</DataLabel>
                      <DataValue>{piece.fabricantAmeublement}</DataValue>
                    </>
                  )}

                  {piece.fabricantInternationalPresence && (
                    <>
                      <DataLabel>{i18n.language === 'fr'
                        ? 'Fabricant - L\'entreprise est-elle présente à l\'internationale ?'
                        : 'Manufacturer - Does the company have an international presence?'}
                      </DataLabel>
                      <DataValue>{piece.fabricantInternationalPresence}</DataValue>
                    </>
                  )}

                  {piece.fabricantInternationalDetails && (
                    <>
                      <DataLabel>{i18n.language === 'fr' ? 'Fabricant - Détails international:' : 'Manufacturer - International details:'}</DataLabel>
                      <DataValue>{piece.fabricantInternationalDetails}</DataValue>
                    </>
                  )}

                  {piece.fabricantWebAndSocial && (
                    <>
                      <DataLabel>{i18n.language === 'fr'
                        ? 'Fabricant - L\'entreprise a-t-elle un site internet à jour, une activité sur les réseaux sociaux dont Instagram ?'
                        : 'Manufacturer - Does the company have an up-to-date website and social media activity including Instagram?'}
                      </DataLabel>
                      <DataValue>{piece.fabricantWebAndSocial}</DataValue>
                    </>
                  )}

                  {piece.fabricantWebAndSocialDetails && (
                    <>
                      <DataLabel>{i18n.language === 'fr' ? 'Fabricant - Détails web/social:' : 'Manufacturer - Web/social details:'}</DataLabel>
                      <DataValue>{piece.fabricantWebAndSocialDetails}</DataValue>
                    </>
                  )}

                  {piece.fabricantPresentation && (
                    <>
                      <DataLabel>{i18n.language === 'fr' ? 'Fabricant - Présentation:' : 'Manufacturer - Presentation:'}</DataLabel>
                      <DataValue>{piece.fabricantPresentation}</DataValue>
                    </>
                  )}
                </>
              )}

              <DataLabel>{i18n.language === 'fr' ? 'Précisez le savoir-faire de la pièce' : 'Specify the craftsmanship of the piece'}</DataLabel>
              <DataValue>{piece.savoirFaire}</DataValue>

              <DataLabel>{i18n.language === 'fr'
                ? 'Précisez en quoi votre pièce s\'inscrit dans une démarche de durabilité (écologique, éthique, qualitative)'
                : 'Specify how your piece fits into a sustainability approach (ecological, ethical, qualitative)'}
              </DataLabel>
              <DataValue>{piece.durability}</DataValue>

              <DataLabel>{i18n.language === 'fr' ? 'Texte de présentation de la pièce' : 'Piece presentation text'}</DataLabel>
              <DataValue style={{ whiteSpace: 'pre-wrap' }}>{piece.presentationText}</DataValue>

              <DataLabel>Dimensions</DataLabel>
              <DataValue>{piece.dimensions}</DataValue>

              <DataLabel>{i18n.language === 'fr' ? 'Matériaux' : 'Materials'}</DataLabel>
              <DataValue>{piece.materials}</DataValue>

              {piece.poids && (
                <>
                  <DataLabel>{i18n.language === 'fr' ? 'Poids:' : 'Weight:'}</DataLabel>
                  <DataValue>{piece.poids}</DataValue>
                </>
              )}
            </DataGrid>

            {piece.uploadedMedia && piece.uploadedMedia.length > 0 && (
              <>
                <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>
                  {i18n.language === 'fr' ? 'Médias uploadés' : 'Uploaded media'} ({piece.uploadedMedia.length})
                </h4>
                <MediaGrid>
                  {piece.uploadedMedia.map((media, idx) => (
                    <MediaCard
                      key={idx}
                      onClick={() => handleMediaClick(`${mediaBaseUrl}/${media.path}`)}
                    >
                      <MediaPreview>
                        {media.mimetype.startsWith('image/') ? (
                          <img
                            src={`${mediaBaseUrl}/${media.path}`}
                            alt={media.originalname}
                          />
                        ) : media.mimetype.startsWith('video/') ? (
                          <div>🎥 {i18n.language === 'fr' ? 'Vidéo' : 'Video'}</div>
                        ) : (
                          <div>📄 PDF</div>
                        )}
                      </MediaPreview>
                      <MediaInfo>
                        <div className="filename">{media.originalname}</div>
                        <div className="size">{(media.size / 1024 / 1024).toFixed(2)} MB</div>
                        {media.credit && (
                          <div className="size" style={{ marginTop: '3px', fontStyle: 'italic' }}>
                            {media.credit}
                          </div>
                        )}
                      </MediaInfo>
                    </MediaCard>
                  ))}
                </MediaGrid>
              </>
            )}
          </PieceCard>
        ))}
      </Section>
    </DetailContainer>
  );
};

export default UserFormSubmissionDetail;